import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, CreditCard, Zap, Wifi, Car, Home, Smartphone, Check, Calendar } from 'lucide-react'

export default function BillsModal({ onClose, user, onPayBill }) {
  const [selectedBill, setSelectedBill] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState('main')
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentComplete, setPaymentComplete] = useState(false)

  const bills = [
    {
      id: 1,
      name: 'Electric Company',
      icon: <Zap className="w-6 h-6" />,
      amount: 85.30,
      dueDate: '2024-02-15',
      status: 'due',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      id: 2,
      name: 'Internet Service',
      icon: <Wifi className="w-6 h-6" />,
      amount: 65.99,
      dueDate: '2024-02-18',
      status: 'upcoming',
      color: 'from-blue-500 to-purple-500'
    },
    {
      id: 3,
      name: 'Car Insurance',
      icon: <Car className="w-6 h-6" />,
      amount: 120.00,
      dueDate: '2024-02-20',
      status: 'upcoming',
      color: 'from-green-500 to-teal-500'
    },
    {
      id: 4,
      name: 'Rent',
      icon: <Home className="w-6 h-6" />,
      amount: 1200.00,
      dueDate: '2024-03-01',
      status: 'upcoming',
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 5,
      name: 'Phone Bill',
      icon: <Smartphone className="w-6 h-6" />,
      amount: 45.00,
      dueDate: '2024-02-25',
      status: 'upcoming',
      color: 'from-indigo-500 to-blue-500'
    }
  ]

  const paymentMethods = [
    { id: 'main', name: 'Main Account', number: '****1234', balance: user.balance },
    { id: 'savings', name: 'Savings Account', number: '****5678', balance: 25000 },
    { id: 'credit', name: 'Credit Card', number: '****9012', balance: 5000 }
  ]

  const handlePayBill = async () => {
    setIsProcessing(true)
    
    setTimeout(() => {
      setIsProcessing(false)
      setPaymentComplete(true)
      
      if (onPayBill) {
        onPayBill({
          ...selectedBill,
          paymentMethod,
          paymentDate: new Date().toISOString().split('T')[0],
          transactionId: `TXN${Date.now().toString().slice(-8)}`
        })
      }
      
      setTimeout(() => {
        onClose()
      }, 2000)
    }, 2000)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'due': return 'text-red-400 bg-red-500/20'
      case 'upcoming': return 'text-blue-400 bg-blue-500/20'
      case 'paid': return 'text-green-400 bg-green-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const getDaysUntilDue = (dueDate) => {
    const today = new Date()
    const due = new Date(dueDate)
    const diffTime = due - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 0) return 'Overdue'
    if (diffDays === 0) return 'Due today'
    if (diffDays === 1) return 'Due tomorrow'
    return `Due in ${diffDays} days`
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="glass max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 border-b border-white/10">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white">Pay Bills</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {paymentComplete ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Payment Successful!</h3>
                <p className="text-gray-400 mb-4">
                  ${selectedBill?.amount} paid to {selectedBill?.name}
                </p>
                <p className="text-sm text-gray-500">
                  Transaction ID: TXN{Date.now().toString().slice(-8)}
                </p>
              </motion.div>
            ) : isProcessing ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-xl font-semibold text-white mb-2">Processing Payment...</h3>
                <p className="text-gray-400">Paying ${selectedBill?.amount} to {selectedBill?.name}</p>
              </motion.div>
            ) : selectedBill ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setSelectedBill(null)}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    ← Back to bills
                  </button>
                </div>

                {/* Bill Details */}
                <div className={`p-6 bg-gradient-to-r ${selectedBill.color} rounded-2xl text-white`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {selectedBill.icon}
                      <h3 className="text-xl font-semibold">{selectedBill.name}</h3>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(selectedBill.status)}`}>
                      {selectedBill.status}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-3xl font-bold">${selectedBill.amount}</p>
                      <p className="text-white/80 flex items-center mt-1">
                        <Calendar className="w-4 h-4 mr-1" />
                        {getDaysUntilDue(selectedBill.dueDate)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">Select Payment Method</h4>
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <button
                        key={method.id}
                        onClick={() => setPaymentMethod(method.id)}
                        className={`w-full p-4 rounded-lg transition-all ${
                          paymentMethod === method.id
                            ? 'bg-blue-600 border-2 border-blue-400'
                            : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <CreditCard className="w-5 h-5 text-white" />
                            <div className="text-left">
                              <p className="text-white font-medium">{method.name}</p>
                              <p className="text-gray-400 text-sm">{method.number}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">${method.balance.toLocaleString()}</p>
                            <p className="text-gray-400 text-sm">Available</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400">Amount:</span>
                    <span className="text-white font-semibold">${selectedBill.amount}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400">Processing Fee:</span>
                    <span className="text-green-400">Free</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-white font-medium">Total:</span>
                      <span className="text-white font-bold text-xl">${selectedBill.amount}</span>
                    </div>
                  </div>
                </div>

                {/* Pay Button */}
                <button
                  onClick={handlePayBill}
                  disabled={!paymentMethod || paymentMethods.find(m => m.id === paymentMethod)?.balance < selectedBill.amount}
                  className="w-full py-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-colors"
                >
                  Pay ${selectedBill.amount}
                </button>

                {paymentMethods.find(m => m.id === paymentMethod)?.balance < selectedBill.amount && (
                  <p className="text-red-400 text-sm text-center">
                    Insufficient funds in selected account
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Your Bills</h3>
                
                {bills.map((bill) => (
                  <motion.button
                    key={bill.id}
                    onClick={() => setSelectedBill(bill)}
                    className="w-full p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-all text-left"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 bg-gradient-to-r ${bill.color} rounded-lg text-white`}>
                          {bill.icon}
                        </div>
                        <div>
                          <h4 className="text-white font-medium">{bill.name}</h4>
                          <p className="text-gray-400 text-sm">{getDaysUntilDue(bill.dueDate)}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-white font-semibold">${bill.amount}</p>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(bill.status)}`}>
                          {bill.status}
                        </span>
                      </div>
                    </div>
                  </motion.button>
                ))}
                
                {/* Summary */}
                <div className="mt-6 p-4 bg-white/5 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Due This Month:</span>
                    <span className="text-white font-bold text-xl">
                      ${bills.reduce((sum, bill) => sum + bill.amount, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}